import { useMemo, useState } from 'react';
import { Icon, Link, Tooltip } from '@nimbus-ds/components';
import { UndoIcon } from '@nimbus-ds/icons';
import {
  FulfillmentOrdersResponseDto,
  OrderDetailsResponseDto,
  OrderFulfillRequestDto,
} from '@tiendanube/common';
import {
  FulfillmentPreferenceType as FulfillmentPreferenceTypeEnum,
  Status,
} from '@tiendanube/common/src/enums';
import { Button, DataList, Stack, Text } from '@tiendanube/components';
import { useResponsive } from 'commons/components';
import { useModal } from 'commons/hooks';
import { calculateDateRange } from 'commons/utils/date';
import { PrintManagerDropdown } from 'domains/FulfillmentOrders/pages/PrintDocumentPage/components';
import { isNuvemEnvioCarrier } from 'domains/NuvemEnvioCommons/Orders/utils';
import ConfirmTrackingNumModal from 'domains/Orders/components/ComfirmTrackingNumModal';
import ConfirmUnpackModal from 'domains/Orders/components/ConfirmUnpackModal';
import {
  trackingOrderEditTrackingCodeClick,
  trackingOrderUnpackClick,
} from 'domains/Orders/tracking';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import { useNuvemApps } from 'domains/PartnersApps/pages/NuvemAppPage/hooks';
import useFulfillmentActionButton from '../../hooks/useFulfillmentActionButton';
import TrackingNumberInfo from '../TrackingNumberInfo';
import './OrderSummaryDelivery.scss';

export const notifyFulfillmentDefault = {
  shippingTrackingNum: '',
  sendNotification: true,
};

interface OrderSummaryDeliveryProps {
  orderDetails: OrderDetailsResponseDto;
  packOrder: () => void;
  fulfillOrder: (order: OrderFulfillRequestDto) => void;
  unpackOrder: () => void;
  isLoading: boolean;
  isUnpacking: boolean;
  fulfillmentOrder?: FulfillmentOrdersResponseDto;
}

function OrderSummaryDelivery({
  orderDetails: {
    id: orderId,
    shipping,
    fulfillment: {
      status,
      preference: {
        option,
        weight,
        shippingMinDays,
        shippingMaxDays,
        type,
        trackingNumber,
        deliveryAddress,
      },
      isShippable,
      pickupDetail,
    },
    urlTrackingCode,
    status: orderStatus,
    isShippablePickup,
    payment,
    customDetails,
  },
  packOrder,
  fulfillOrder,
  unpackOrder,
  isLoading,
  isUnpacking,
  fulfillmentOrder,
}: OrderSummaryDeliveryProps): JSX.Element {
  const t = useTranslationOrders();
  const { isNuvemEnvioInstalled } = useNuvemApps();

  const { isDesktop } = useResponsive();

  const [isShow, openModal, closeModal] = useModal();
  const [isShowUnpack, openUnpackModal, closeUnpackModal] = useModal();
  const [isEditing, setIsEditing] = useState(false);

  const shippingStatus = fulfillmentOrder?.status || status;
  const shippingType = fulfillmentOrder?.shipping?.type || type;

  const { secondaryButton, handleClickFulfill } = useFulfillmentActionButton({
    fulfillOrder,
    packOrder,
    openModal,
    closeModal,
    status: shippingStatus,
    type: shippingType as FulfillmentPreferenceTypeEnum,
    isLoading,
    isUnpacking: isUnpacking ?? false,
    orderStatus,
    isShippablePickup,
  });

  const getShippingDays = () => {
    if (fulfillmentOrder) {
      const dateRange = calculateDateRange(
        fulfillmentOrder.createdAt,
        fulfillmentOrder.shipping.minDeliveryDate ?? null,
        fulfillmentOrder.shipping.maxDeliveryDate ?? null,
      );
      return {
        minDays: dateRange.min_days,
        maxDays: dateRange.max_days,
      };
    } else {
      return {
        minDays: shippingMinDays,
        maxDays: shippingMaxDays,
      };
    }
  };

  const getCustomRanges = () => {
    if (fulfillmentOrder) {
      return [
        {
          minRange: fulfillmentOrder.shipping?.customDetails?.minRange ?? null,
          maxRange: fulfillmentOrder.shipping?.customDetails?.maxRange ?? null,
        },
      ].filter((range) => range.minRange !== null || range.maxRange !== null);
    } else {
      return customDetails?.filter(
        (customDetail) =>
          customDetail.minRange !== null || customDetail.maxRange !== null,
      );
    }
  };

  const handleOpenModal = (editing = false) => {
    editing && trackingOrderEditTrackingCodeClick();
    setIsEditing(editing);
    openModal();
  };

  const handleOpenUnpackModal = () => {
    if (isUnpacking) return;
    trackingOrderUnpackClick();
    openUnpackModal();
  };

  const { minDays: shippingMinDaysFinal, maxDays: shippingMaxDaysFinal } =
    getShippingDays();

  const customRanges = getCustomRanges();

  const hasCustomShippingRange = customRanges && customRanges?.length > 0;

  const checkShippingDays = (): boolean =>
    shippingMinDaysFinal !== undefined &&
    shippingMinDaysFinal > 0 &&
    shippingMaxDaysFinal !== undefined &&
    shippingMaxDaysFinal > 0;

  const hasShippingDays: boolean = checkShippingDays();
  const isShippingDaysRange = shippingMinDaysFinal !== shippingMaxDaysFinal;
  const showPickUpAdress = pickupDetail?.address;

  const [trackingCode, trackingUrl] = fulfillmentOrder
    ? [fulfillmentOrder.trackingInfo.code, fulfillmentOrder.trackingInfo.url]
    : [trackingNumber, urlTrackingCode];
  const shippingOption = fulfillmentOrder
    ? fulfillmentOrder.shipping.option.name
    : option;

  const totalWeight = fulfillmentOrder ? fulfillmentOrder.totalWeight : weight;

  const getOptionOrShippingOption = () => shippingOption || option;

  const getTranslationOrOption = () =>
    shippingOption
      ? t('cardFulfillment.pickerFrom', { from: shippingOption })
      : option;

  const getPickupOptionText = () =>
    isShippablePickup ? getOptionOrShippingOption() : getTranslationOrOption();

  const getNonPickupOptionText = () =>
    fulfillmentOrder?.shipping?.carrier?.name &&
    shippingOption &&
    !shippingOption.includes(fulfillmentOrder.shipping.carrier.name)
      ? `${fulfillmentOrder.shipping.carrier.name} - ${shippingOption}`
      : shippingOption;

  const getShippingOptionText = () => {
    if (!fulfillmentOrder) return shippingOption;
    if (shippingType === FulfillmentPreferenceTypeEnum.PICKUP)
      return getPickupOptionText();
    if (shippingType !== FulfillmentPreferenceTypeEnum.NON_SHIPPABLE)
      return getNonPickupOptionText();
    return shippingOption;
  };

  const sameOptionNameAndPickupDetailName =
    getShippingOptionText() === pickupDetail?.name;

  const isDigital =
    shippingType === FulfillmentPreferenceTypeEnum.NON_SHIPPABLE ||
    shippingType === FulfillmentPreferenceTypeEnum.DELIVERY_DIGITAL;

  const isShowableTrackingInfo = useMemo(
    () =>
      ([
        Status.MARKED_AS_FULFILLED,
        Status.DELIVERED,
        Status.FULFILLED,
        Status.DISPATCHED,
        Status.SHIPPED,
      ].includes(shippingStatus as Status) &&
        shippingType !== FulfillmentPreferenceTypeEnum.PICKUP) ||
      !!trackingCode?.trim() ||
      !!trackingUrl,
    [shippingStatus, shippingType, trackingCode, trackingUrl],
  );

  const isEditableTrackingInfo =
    ![Status.DELIVERED, Status.UNPACKED, Status.PACKED].includes(
      shippingStatus as Status,
    ) || isDigital;

  const isPackedNonDigitalDelivery =
    !isDigital && (shippingStatus as Status) === Status.PACKED;

  return (
    <>
      <div className="summaryDelivery">
        <DataList.Row id="bottom">
          <DataList.Cell width="fill">
            <Stack column align="stretch" spacing="base">
              <Stack>
                {isShippable && (
                  <Stack.Item>
                    <Text block bold appearance="default">
                      {getShippingOptionText()}
                    </Text>

                    {!!showPickUpAdress && (
                      <>
                        {!sameOptionNameAndPickupDetailName && (
                          <Text block>{pickupDetail.name}</Text>
                        )}
                        <Text block>{pickupDetail.address}</Text>
                      </>
                    )}

                    <Stack column={false} spacing="none" align="flex-start">
                      {hasCustomShippingRange &&
                        customRanges.map((range, index) => (
                          <Text
                            block
                            key={`${range.minRange}-${range.maxRange}`}
                          >
                            {range.minRange === null || range.minRange === 0
                              ? t('cardFulfillment.distanceRangeMaxOnly', {
                                  maxRange: range.maxRange,
                                })
                              : t('cardFulfillment.distanceRange', {
                                  minRange: range.minRange,
                                  maxRange: range.maxRange,
                                })}
                            {(index < customRanges.length - 1 ||
                              hasShippingDays) && <>&nbsp;-&nbsp;</>}
                          </Text>
                        ))}

                      {hasShippingDays &&
                        (isShippingDaysRange ? (
                          <Text block>
                            {t('cardFulfillment.estimatedTimeRange', {
                              minDays: shippingMinDaysFinal,
                              maxDays: shippingMaxDaysFinal,
                            })}
                          </Text>
                        ) : (
                          <Text block>
                            {t('cardFulfillment.estimatedTime', {
                              count: shippingMinDaysFinal,
                            })}
                          </Text>
                        ))}
                    </Stack>

                    {!!totalWeight && (
                      <Text block>
                        {t('cardFulfillment.orderWeight', {
                          weight: totalWeight,
                        })}
                      </Text>
                    )}
                  </Stack.Item>
                )}
              </Stack>
              <Stack
                column={!isDesktop}
                justify="flex-start"
                align="flex-start"
                spacing="tight"
              >
                {fulfillmentOrder ? (
                  <PrintManagerDropdown
                    fulfillmentOrderId={fulfillmentOrder.id}
                    orderId={orderId}
                    shipping={shipping}
                    hasGeneratedTag={deliveryAddress?.hasGeneratedTag || false}
                    isNuvemEnvio={Boolean(
                      isNuvemEnvioInstalled &&
                        fulfillmentOrder?.shipping?.carrier?.name &&
                        isNuvemEnvioCarrier(
                          fulfillmentOrder.shipping.carrier.name,
                          shipping,
                        ) &&
                        payment.status === Status.PAID,
                    )}
                    paymentStatus={payment.status}
                  />
                ) : (
                  <PrintManagerDropdown
                    orderId={orderId}
                    shipping={shipping}
                    hasGeneratedTag={deliveryAddress?.hasGeneratedTag || false}
                    paymentStatus={payment.status}
                  />
                )}
                {!!secondaryButton?.children && (
                  <Button {...secondaryButton} appearance="primary">
                    {secondaryButton?.children}
                  </Button>
                )}
              </Stack>
              {isPackedNonDigitalDelivery && (
                <div className="extraFooter">
                  <Tooltip
                    arrow
                    position="right"
                    content={t('cardFulfillment.unpackTooltip')}
                  >
                    <Link
                      textDecoration="none"
                      appearance="primary"
                      onClick={handleOpenUnpackModal}
                    >
                      <Icon color="primary-interactive" source={<UndoIcon />} />
                      {t('cardFulfillment.unpack')}
                    </Link>
                  </Tooltip>
                </div>
              )}
              {isShowableTrackingInfo && (
                <div className="extraFooter">
                  <TrackingNumberInfo
                    trackingNumber={trackingCode}
                    urlTrackingCode={trackingUrl}
                    isDigital={isDigital}
                    showEdit={isEditableTrackingInfo}
                    onClick={() => handleOpenModal(true)}
                  />
                </div>
              )}
            </Stack>
          </DataList.Cell>
        </DataList.Row>
      </div>

      {isShow && (
        <ConfirmTrackingNumModal
          onClose={closeModal}
          onConfirm={handleClickFulfill}
          shippingTrackingNum={trackingCode || ''}
          isDigital={isDigital}
          isEditing={isEditing}
        />
      )}

      {isShowUnpack && (
        <ConfirmUnpackModal
          onClose={closeUnpackModal}
          onConfirm={unpackOrder}
        />
      )}
    </>
  );
}
export default OrderSummaryDelivery;
